/**
 * PlaidSupportActions.tsx
 * 
 * Component for facilitating Plaid support ticket submission.
 * Provides three action paths:
 * 1. Deep link to Plaid Dashboard support form
 * 2. Copy all support data to clipboard
 * 3. Email Plaid support (mailto: link)
 */

import { FC, useState } from 'react';
import { FiClipboard, FiMail, FiExternalLink, FiCheck } from 'react-icons/fi';
import { ConnectionCase, PlaidLinkEvent, PlaidLinkSession } from '../../services/plaidConnectionMonitorService';

interface PlaidSupportActionsProps {
  selectedCase: ConnectionCase;
  events: PlaidLinkEvent[];
  session?: PlaidLinkSession | null;
  errorCode?: string | null;
  institutionId?: string | null;
  institutionUrl?: string | null;  // Added: Institution URL for Plaid support
}

const PlaidSupportActions: FC<PlaidSupportActionsProps> = ({
  selectedCase,
  events,
  session,
  errorCode,
  institutionId,
  institutionUrl
}) => {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showManualCopy, setShowManualCopy] = useState(false);

  // Extract required Plaid support data
  const errorEvent = events.find(e => e.error_code || e.error_type);
  const actualErrorCode = errorCode || errorEvent?.error_code || 'Unknown';
  const actualErrorType = errorEvent?.error_type || 'Unknown';
  const requestId = errorEvent?.request_id || 'Not available';
  const linkSessionId = session?.link_session_id || 'Not available';
  const actualInstitutionId = institutionId || errorEvent?.institution_id || 'Unknown';
  const institutionName = selectedCase.institution_name || 'Unknown';
  const memberEmail = selectedCase.member_email || 'Not available';
  const memberName = selectedCase.member_name || 'Not available';
  const timestamp = new Date(selectedCase.created_at).toLocaleString();
  const errorMessage = errorEvent?.error_message_sanitized || 'Not available';
  const retryAttempt = selectedCase.retry_attempt_number || 1;
  const memberImpact = selectedCase.member_impact || 'Not assessed';
  const environment = import.meta.env.VITE_PLAID_ENV || 'development';

  // Build event sequence for context
  const eventSequence = events
    .map(e => `${e.event_name}${e.institution_name ? ` (${e.institution_name})` : ''}`)
    .join(' → ');

  // Generate Google search link for institution URL if not available
  const googleSearchUrl = institutionName !== 'Unknown'
    ? `https://www.google.com/search?q=${encodeURIComponent(institutionName + ' official website')}`
    : null;

  // Generate support data text for clipboard
  const generateSupportData = () => {
    return `=== PLAID SUPPORT TICKET DATA ===

Institution Name: ${institutionName}
Institution ID: ${actualInstitutionId}
${institutionUrl ? `Institution URL: ${institutionUrl}` : `Institution URL: [Look up at: ${googleSearchUrl}]`}
Error Code: ${actualErrorCode}
Error Type: ${actualErrorType}
Error Message: ${errorMessage}

Link Session ID: ${linkSessionId}
Request ID: ${requestId}
Member Name: ${memberName}
Member Email: ${memberEmail}
Case ID: ${selectedCase.id}
Timestamp: ${timestamp}
Environment: ${environment}

=== DESCRIPTION ===
Member attempted to connect their ${institutionName} account and received ${actualErrorCode} error.
${retryAttempt > 1 ? `This is retry attempt #${retryAttempt} - member has tried ${retryAttempt} time(s).` : 'This is the first connection attempt.'}

Classification: ${selectedCase.classification || 'Not classified'}
Status: ${selectedCase.status}
Priority: ${selectedCase.priority}
Member Impact: ${memberImpact}

=== REQUEST ===
${actualErrorCode === 'INSTITUTION_NO_LONGER_SUPPORTED'
  ? 'Please investigate if alternative institution entries exist or if support can be restored for this institution.'
  : `Please investigate this ${actualErrorCode} error and provide guidance on resolution.`}

=== SUBMIT TICKET ===
Plaid Dashboard: https://dashboard.plaid.com/support/new/financial-institutions

=== ADDITIONAL CONTEXT ===
Total Events: ${events.length} recorded for this session
Event Sequence: ${eventSequence}
Error Occurred At: ${errorEvent?.event_timestamp || 'Unknown'}
${retryAttempt > 1 ? `Retry History: Member has attempted ${retryAttempt} times` : ''}
`;
  };

  // Copy to clipboard with fallback
  const handleCopyData = async () => {
    const text = generateSupportData();

    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
        return;
      } catch (err) {
        console.warn('Clipboard API failed, trying fallback:', err);
      }
    }

    // Fallback for older browsers or non-HTTPS contexts
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      } else {
        throw new Error('execCommand copy failed');
      }
    } catch (err) {
      console.error('Copy failed:', err);
      alert('Failed to copy to clipboard. Please try manually selecting and copying the data from the support section below.');
    }
  };

  // Generate mailto: link
  const handleEmailPlaid = () => {
    const subject = `Institution Support Request: ${institutionName} (${actualInstitutionId})`;
    const body = `Institution: ${institutionName}
Institution ID: ${actualInstitutionId}
${institutionUrl ? `Institution URL: ${institutionUrl}` : `Institution URL: [Please look up and add]`}
Error Code: ${actualErrorCode}
Error Type: ${actualErrorType}
Error Message: ${errorMessage}

Link Session ID: ${linkSessionId}
Request ID: ${requestId}
Member Name: ${memberName}
Member Email: ${memberEmail}
Occurred: ${timestamp}
Environment: ${environment}

Details:
Member attempted to connect ${institutionName} and received ${actualErrorCode} error.
${retryAttempt > 1 ? `This is retry attempt #${retryAttempt}.` : ''}

${actualErrorCode === 'INSTITUTION_NO_LONGER_SUPPORTED'
  ? 'Request: Please investigate if alternative institution entries exist or if support can be restored.'
  : `Request: Please investigate this error and provide guidance.`}

Case ID: ${selectedCase.id}
Classification: ${selectedCase.classification || 'Not classified'}
Priority: ${selectedCase.priority}
Member Impact: ${memberImpact}

Event Sequence: ${eventSequence}`;

    const mailto = `mailto:fi-support@plaid.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  };

  // Open Plaid Dashboard
  const handleOpenDashboard = () => {
    window.open('https://dashboard.plaid.com/support/new/financial-institutions', '_blank');
  };

  // Check if this is a critical institution error
  const criticalErrors = [
    'INSTITUTION_NO_LONGER_SUPPORTED',
    'INSTITUTION_NOT_FOUND',
    'INSTITUTION_ERROR',
    'INSTITUTION_NOT_RESPONDING'
  ];
  const isCritical = actualErrorCode && criticalErrors.includes(actualErrorCode);

  // Auto-expand for critical errors
  if (isCritical && !expanded) {
    setExpanded(true);
  }

  return (
    <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-lg border border-slate-200">
      {/* Header - Always Visible (Collapsible) */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">🆘</span>
          <h3 className="text-sm font-bold text-slate-700">
            Report to Plaid Support
          </h3>
          {isCritical && (
            <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full font-medium">
              Recommended
            </span>
          )}
        </div>
        <span className="text-slate-400">
          {expanded ? '▲' : '▼'}
        </span>
      </button>

      {/* Expandable Content */}
      {expanded && (
        <div className="px-5 pb-5">
          <p className="text-xs text-slate-600 mb-4">
            {isCritical
              ? 'This case requires Plaid assistance. All required support data has been collected.'
              : 'Submit a support ticket to Plaid with all case details pre-filled.'}
          </p>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 gap-2 mb-4">
        <button
          onClick={handleOpenDashboard}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <FiExternalLink className="w-4 h-4" />
          Open Plaid Support Form
        </button>

        <button
          onClick={handleCopyData}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-600 hover:bg-slate-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          {copied ? <FiCheck className="w-4 h-4" /> : <FiClipboard className="w-4 h-4" />}
          {copied ? 'Copied!' : 'Copy Support Data'}
        </button>

        <button
          onClick={handleEmailPlaid}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <FiMail className="w-4 h-4" />
          Email Plaid Support
        </button>
      </div>

      {/* Manual Copy Fallback - Expandable Text Area */}
      <div className="mt-4 bg-white rounded-lg border border-slate-200">
        <button
          onClick={() => setShowManualCopy(!showManualCopy)}
          className="w-full px-3 py-2 flex items-center justify-between hover:bg-slate-50 transition-colors text-xs"
        >
          <span className="font-medium text-slate-700">
            {showManualCopy ? '▼' : '▶'} View/Copy Support Data Manually
          </span>
          <span className="text-slate-500 text-xs">
            {showManualCopy ? 'Hide' : 'Show'}
          </span>
        </button>

        {showManualCopy && (
          <div className="px-3 pb-3">
            <textarea
              readOnly
              value={generateSupportData()}
              className="w-full h-64 px-3 py-2 text-xs font-mono bg-slate-50 border border-slate-300 rounded text-slate-800 resize-none"
              onClick={(e) => {
                (e.target as HTMLTextAreaElement).select();
              }}
            />
            <p className="text-xs text-slate-500 mt-2">
              💡 Click inside the text area to select all, then press Ctrl+C (or Cmd+C) to copy
            </p>
          </div>
        )}
      </div>

      {/* Data Collection Status */}
      <div className="bg-white rounded-lg p-3 border border-slate-200 mt-4">
        <p className="text-xs font-semibold text-slate-600 mb-2">📊 Support Data Collected:</p>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-1.5">
            <span className={linkSessionId !== 'Not available' ? 'text-green-600' : 'text-amber-600'}>
              {linkSessionId !== 'Not available' ? '✅' : '⚠️'}
            </span>
            <span className="text-slate-600">Link Session ID</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={requestId !== 'Not available' ? 'text-green-600' : 'text-amber-600'}>
              {requestId !== 'Not available' ? '✅' : '⚠️'}
            </span>
            <span className="text-slate-600">Request ID</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={actualInstitutionId !== 'Unknown' ? 'text-green-600' : 'text-amber-600'}>
              {actualInstitutionId !== 'Unknown' ? '✅' : '⚠️'}
            </span>
            <span className="text-slate-600">Institution ID</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={actualErrorCode !== 'Unknown' ? 'text-green-600' : 'text-amber-600'}>
              {actualErrorCode !== 'Unknown' ? '✅' : '⚠️'}
            </span>
            <span className="text-slate-600">Error Code</span>
          </div>
        </div>
      </div>

      {/* Quick Info */}
      <div className="mt-3 text-xs text-slate-500">
        <p className="mb-1">
          <strong>Institution ID:</strong> {actualInstitutionId}
        </p>
        <p className="mb-1">
          <strong>Error:</strong> {actualErrorCode}
        </p>
        {linkSessionId !== 'Not available' && (
          <p className="mb-1">
            <strong>Link Session:</strong> {linkSessionId}
          </p>
        )}
        {!institutionUrl && googleSearchUrl && (
          <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded">
            <p className="text-amber-800 font-semibold mb-1">⚠️ Institution URL Not Available</p>
            <p className="text-amber-700 mb-2">
              The institution URL will be needed for the Plaid support form.
            </p>
            <a
              href={googleSearchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-medium transition-colors"
            >
              <FiExternalLink size={12} />
              Look Up "{institutionName}" Website
            </a>
            <p className="text-amber-600 mt-2 text-xs">
              💡 Copy the official website URL and paste it into the Plaid form's "Institution homepage URL" field
            </p>
          </div>
        )}
      </div>
        </div>
      )}
    </div>
  );
};

export default PlaidSupportActions;
